import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import GameHeader from "../components/GameHeader";
import LevelComplete from "../components/LevelComplete";
import FeedingMonsterGame from "../components/games/FeedingMonsterGame";
import BridgeBuilderGame from "../components/games/BridgeBuilderGame";
import BubblePopGame from "../components/games/BubblePopGame";
import MagicWandGame from "../components/games/MagicWandGame";
import ReaderGame from "../components/games/ReaderGame";
import DigraphGame from "../components/games/DigraphGame";
import { getLevel, LEVELS, nextLevelId } from "../data/curriculum";
import { useProfile } from "../context/ProfileContext";
import { useAuth } from "../context/AuthContext";

const FREE_LEVEL_ID = 1;

const GAME_MAP = {
  feeding: FeedingMonsterGame,
  bridge: BridgeBuilderGame,
  bubble: BubblePopGame,
  magic: MagicWandGame,
  reader: ReaderGame,
  digraph: DigraphGame,
};

export default function GamePage() {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const { current, saveProgress, loading } = useProfile();
  const { user } = useAuth();
  const level = getLevel(levelId);
  const [result, setResult] = useState(null);
  const [key, setKey] = useState(0); // force remount on replay

  const premium = !!(user?.premium || user?.subscription?.active || user?.role === "admin");

  useEffect(() => {
    if (!loading && !current) navigate("/profiles");
  }, [current, loading, navigate]);

  useEffect(() => {
    if (level && !premium && level.id !== FREE_LEVEL_ID) navigate("/parent");
  }, [level, premium, navigate]);

  if (!level || !current) return null;
  const GameComp = GAME_MAP[level.game];
  const hasNext = nextLevelId(level.id) !== null;

  const onFinish = async ({ stars, accuracy }) => {
    setResult({ stars, accuracy, newStickers: [] });
    try {
      const updated = await saveProgress(level.id, stars, accuracy, { [level.phase]: accuracy });
      setResult({ stars, accuracy, newStickers: updated?.new_stickers || [] });
      toast.success(`You earned ${stars} ⭐!`);
    } catch (e) {
      toast.error("Could not save progress, but great job!");
    }
  };

  const replay = () => {
    setResult(null);
    setKey((k) => k + 1);
  };

  const next = () => {
    const nid = nextLevelId(level.id);
    setResult(null);
    setKey((k) => k + 1);
    if (nid) navigate(`/game/${nid}`);
    else navigate("/map");
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#BAE6FD 0%,#E0F2FE 40%,#DCFCE7 100%)" }}>
      <GameHeader title={level.title} color={level.color} />
      <main className="relative">
        <GameComp key={key} level={level} onFinish={onFinish} />
      </main>
      {result && (
        <LevelComplete
          stars={result.stars}
          hasNext={hasNext}
          newStickers={result.newStickers}
          onReplay={replay}
          onNext={next}
          onHome={() => navigate("/map")}
        />
      )}
    </div>
  );
}
